import React, { useState } from 'react';
import { PieChart, Download, Info } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения детальной разбивки затрат трансформации
 */
const DetailedBreakdown = ({ 
  costBreakdown, 
  results, 
  showDetailedBreakdown, 
  setShowDetailedBreakdown 
}) => {
  const [activeTab, setActiveTab] = useState('chart');
  
  if (!costBreakdown || costBreakdown.length === 0 || !results) {
    return null;
  }

  // Применяем цвета к различным категориям для визуализации
  const categoryColors = {
    "Внутренняя команда": "bg-blue-500",
    "Консалтинговые услуги": "bg-purple-500",
    "Программное обеспечение": "bg-green-500",
    "Оборудование и инфраструктура": "bg-yellow-500",
    "Обучение персонала": "bg-pink-500",
    "Резерв на непредвиденные расходы": "bg-orange-500"
  };

  // Рассчитываем общую стоимость
  const totalCost = results.totalCost;

  // Отображаем полную или краткую разбивку затрат
  const displayedBreakdown = costBreakdown;

  // Создаем данные для диаграммы
  const chartData = costBreakdown.map((item, index) => ({
    ...item,
    color: Object.values(categoryColors)[index % Object.values(categoryColors).length]
  }));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <PieChart className="w-5 h-5 mr-2 text-indigo-500" />
          Детальная разбивка затрат
        </h3>

        <Button
          size="sm"
          onClick={() => setShowDetailedBreakdown(prev => !prev)}
          variant="outline"
          aria-expanded={showDetailedBreakdown}
        >
          {showDetailedBreakdown ? 'Скрыть детали' : 'Показать подробнее'}
        </Button>
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('chart')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'chart'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Диаграмма
        </button>
        <button
          onClick={() => setActiveTab('table')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'table'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Таблица затрат
        </button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>Визуализация структуры затрат на трансформацию бизнес-процессов. Используйте эти данные для анализа бюджета проекта и выявления ключевых факторов затрат.</p>
        </div>
      </div>

      {/* Контент в зависимости от активной вкладки */}
      {activeTab === 'chart' && (
        <div className="mb-6">
          {/* Визуализация соотношения затрат */}
          <div className="flex flex-col md:flex-row gap-6 items-center justify-center mb-6">
            {/* Диаграмма */}
            <div className="relative w-64 h-64">
              <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                {chartData.reduce((elements, item, index, array) => {
                  const previousSum = array
                    .slice(0, index)
                    .reduce((sum, i) => sum + i.percentage, 0);
                  
                  const percentage = item.percentage;
                  
                  // Расчет координат для создания сектора
                  const startAngle = (previousSum / 100) * 360;
                  const endAngle = ((previousSum + percentage) / 100) * 360;
                  
                  // Расчет координат для пути SVG
                  const startX = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
                  const startY = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
                  const endX = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
                  const endY = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);
                  
                  // Определение большой дуги (1 если > 180 градусов)
                  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
                  
                  // Создание пути
                  const pathData = [
                    `M 50 50`,
                    `L ${startX} ${startY}`,
                    `A 40 40 0 ${largeArcFlag} 1 ${endX} ${endY}`,
                    `Z`
                  ].join(' ');

                  const colorClass = item.color.replace('bg-', 'fill-');
                  
                  elements.push(
                    <path
                      key={index}
                      d={pathData}
                      className={colorClass}
                      stroke="white"
                      strokeWidth="0.5"
                    />
                  );
                  
                  return elements;
                }, [])}
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(totalCost, 0)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Всего
                  </div>
                </div>
              </div>
            </div>

            {/* Легенда */}
            <div className="space-y-3">
              {chartData.map((item, index) => (
                <div key={index} className="flex items-center">
                  <div className={`w-4 h-4 rounded ${item.color} mr-2`}></div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {item.category}
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        {formatNumber(item.percentage, 1)}%
                      </span>
                      <span className="text-gray-800 dark:text-gray-200 font-medium">
                        {formatCurrency(item.cost, 0)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Информация о распределении затрат */}
          {showDetailedBreakdown && (
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Анализ структуры затрат:</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Наибольшую долю в бюджете ({formatNumber(chartData[0].percentage, 1)}%) составляют затраты на <strong>{chartData[0].category.toLowerCase()}</strong>. 
                {chartData[1] && ` Далее следуют расходы на ${chartData[1].category.toLowerCase()} (${formatNumber(chartData[1].percentage, 1)}%).`}
              </p>
              
              {results.roi && results.roi.paybackPeriodMonths && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Учитывая ожидаемые выгоды, эти инвестиции должны окупиться примерно через 
                  <strong> {Math.round(results.roi.paybackPeriodMonths)} месяцев</strong> 
                  {' '} ({(results.roi.paybackPeriodMonths / 12).toFixed(1)} лет).
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'table' && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 cost-breakdown-table">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[600px]">
            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
              <tr>
                <th scope="col" className="px-4 py-2 font-semibold">Категория затрат</th>
                <th scope="col" className="px-4 py-2 font-semibold text-right">Сумма (руб.)</th>
                <th scope="col" className="px-4 py-2 font-semibold text-right">% от общей стоимости</th>
                {showDetailedBreakdown && (
                  <th scope="col" className="px-4 py-2 font-semibold text-right">Стоимость в месяц (руб.)</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {displayedBreakdown.map((item, index) => (
                <tr 
                  key={index}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100"
                >
                  <td className="px-4 py-2 whitespace-nowrap font-medium flex items-center">
                    <div className={`w-3 h-3 rounded ${chartData[index]?.color || 'bg-gray-500'} mr-2`}></div>
                    {item.category}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-right font-medium">
                    {formatCurrency(item.cost, 0)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-right">
                    {formatNumber(item.percentage, 1)}%
                  </td>
                  {showDetailedBreakdown && (
                    <td className="px-4 py-2 whitespace-nowrap text-right text-gray-600 dark:text-gray-400">
                      {formatCurrency(item.cost / results.projectParameters.projectDurationMonths, 0)}
                    </td>
                  )}
                </tr>
              ))}
              
              {/* Итоговая строка */}
              <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
                <td className="px-4 py-2 whitespace-nowrap">ИТОГО:</td>
                <td className="px-4 py-2 whitespace-nowrap text-right">{formatCurrency(totalCost, 0)}</td>
                <td className="px-4 py-2 whitespace-nowrap text-right">100%</td>
                {showDetailedBreakdown && (
                  <td className="px-4 py-2 whitespace-nowrap text-right">
                    {formatCurrency(totalCost / results.projectParameters.projectDurationMonths, 0)}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Кнопка переключения подробностей */}
      {!showDetailedBreakdown && (
        <div className="text-center mt-4">
          <Button
            onClick={() => setShowDetailedBreakdown(true)}
            icon={<Download className="w-4 h-4" />}
            variant="link"
          >
            Показать больше деталей
          </Button>
        </div>
      )}
    </div>
  );
};

export default DetailedBreakdown;